import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';

export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/');
    } else {
      toast.error(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#080C0A' }}>
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(3, 32, 20, 0.4) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)'
        }} />
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#032014] rounded-full filter blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#D4AF37] rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center justify-center mb-6"
            >
              <Logo size={88} animated={false} />
            </motion.div>
            <h1 className="text-5xl font-light mb-3" style={{ fontFamily: "'Playfair Display', serif", color: '#F8FAF9' }}>
              Welcome Back
            </h1>
            <p className="text-sm tracking-[0.2em] uppercase" style={{ color: '#A3B8AC' }}>Sign in to LimHappyShop</p>
          </div>

          {/* Login Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="rounded-2xl p-8 backdrop-blur-2xl"
            style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
          >
            <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-6">
              <div>
                <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#A3B8AC' }} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg pl-12 pr-4 py-3.5 transition-all duration-300 focus:ring-1 outline-none"
                    style={{ 
                      background: 'rgba(0, 0, 0, 0.2)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#F8FAF9'
                    }}
                    placeholder="your@email.com"
                    required
                    data-testid="email-input"
                    onFocus={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 1)'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#A3B8AC' }} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg pl-12 pr-4 py-3.5 transition-all duration-300 focus:ring-1 outline-none"
                    style={{ 
                      background: 'rgba(0, 0, 0, 0.2)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#F8FAF9'
                    }}
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                    onFocus={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 1)'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                  />
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full rounded-full px-8 py-4 font-medium transition-all duration-300 disabled:opacity-50"
                style={{ background: '#D4AF37', color: '#080C0A' }}
                data-testid="login-submit-btn"
                onMouseEnter={(e) => e.target.style.background = '#F0C955'}
                onMouseLeave={(e) => e.target.style.background = '#D4AF37'}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </motion.button>
            </form>

            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-xs" style={{ color: '#A3B8AC' }} data-testid="forgot-password-link">
                Lupa password?
              </Link>
            </div>

            <div className="mt-6 text-center">
              <p style={{ color: '#A3B8AC' }} className="text-sm">
                Don't have an account?{' '}
                <Link to="/register" className="font-medium transition-colors" style={{ color: '#D4AF37' }} data-testid="register-link">
                  Create Account
                </Link>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};